/* ═══════════════════════════════════════════════════════════════════
   CreatorKpiPreview — pitch-friendly "See Creator View" card.

   Shows exactly what a creator sees inside their RFP/inbox:
     • Campaign brief header (brand, goal, role, angle, hook)
     • "Here's what the brand expects from you" — per-KPI rows with:
         - Personal target (their weighted share of the total)
         - Current standing (live or simulated)
         - Status badge (met | on_track | at_risk | behind | pending)
     • Leaderboard rank out of live peers ("You're #4 of 18 firing right now")

   Used in:
     - RfpPreviewModal (per-RFP "See Creator View" toggle — draft waves)
     - LiveWaveDashboard LivePostRow (per-live-post toggle — sent waves)
   ═══════════════════════════════════════════════════════════════════ */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Trophy, Target, CheckCircle2, AlertTriangle, TrendingUp, Clock, User, Hash,
  Loader2, DollarSign, Users as UsersIcon,
} from 'lucide-react';
import { T, P, CARD, BORDER } from './constants';
import { BENCHMARK_CATALOG } from './KpiBenchmarkBuilder';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  met:       { label: 'Crushing it',  color: '#4ade80', bg: 'rgba(74,222,128,0.12)',  icon: Trophy },
  on_track:  { label: 'On pace',      color: '#00F5FF', bg: 'rgba(0,245,255,0.10)',   icon: TrendingUp },
  at_risk:   { label: 'At risk',      color: '#FFB84D', bg: 'rgba(255,184,77,0.12)',  icon: AlertTriangle },
  behind:    { label: 'Behind',       color: '#FF5555', bg: 'rgba(255,85,85,0.12)',   icon: AlertTriangle },
  pending:   { label: 'Upcoming',     color: '#888',    bg: 'rgba(136,136,136,0.1)',  icon: Clock },
};

/**
 * Props:
 *   - waveId (string, required)
 *   - assignmentId (string, required)
 *   - compact (bool) — collapsed inline card mode
 */
export default function CreatorKpiPreview({ waveId, assignmentId, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!waveId || !assignmentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await axios.get(`${API}/api/brand/waves/${waveId}/creator-view/${assignmentId}`, { withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || 'Failed to load creator view');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [waveId, assignmentId]);

  if (loading) {
    return (
      <div className="rounded-lg p-4 flex items-center justify-center gap-2 text-[11px] text-gray-500" data-testid="creator-kpi-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading creator view…
      </div>
    );
  }
  if (err) {
    return (
      <div className="rounded-lg p-3 text-[10px] text-red-400" style={{ background: 'rgba(255,85,85,0.06)', border: '1px solid rgba(255,85,85,0.2)' }}
           data-testid="creator-kpi-error">
        {err}
      </div>
    );
  }
  if (!data) return null;

  const { creator, wave, benchmarks, leaderboard_rank, leaderboard_total, follower_weight_pct } = data;

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: CARD, border: `1px solid ${T}30` }} data-testid="creator-kpi-preview">
      {/* Header: what the creator sees first */}
      <div className="px-4 py-3" style={{ background: `linear-gradient(135deg, ${T}10, ${P}08)`, borderBottom: `1px solid ${BORDER}` }}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[8px] px-1.5 py-0.5 rounded-full font-black uppercase tracking-widest"
                style={{ background: `${P}22`, color: P, border: `1px solid ${P}40` }}>
            Creator View
          </span>
          <span className="text-[9px] text-gray-500 uppercase tracking-wider font-bold">What {creator.handle} sees in their RFP</span>
        </div>
        <p className="text-sm text-white font-bold leading-tight">
          Hey {creator.name?.split(' ')[0] || 'there'} — <span className="text-[#00F5FF]">{wave.brand_name || 'Brand'}</span> wants you in this Wave.
        </p>
        <p className="text-[11px] text-gray-400 mt-1 leading-snug">{wave.goal}</p>

        {/* Role/angle/hook row */}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {creator.role && (
            <span className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase flex items-center gap-1"
                  style={{ background: creator.role === 'patient_zero' ? 'rgba(255,107,189,0.18)' : 'rgba(125,232,140,0.15)',
                           color: creator.role === 'patient_zero' ? '#FF6BBD' : '#7DE88C' }}>
              <User className="h-2.5 w-2.5" />
              {creator.role === 'patient_zero' ? 'Patient Zero' : creator.role.replace(/_/g, ' ')}
            </span>
          )}
          {creator.angle && (
            <span className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase" style={{ background: '#1a1a1a', color: T }}>
              {creator.angle.replace(/_/g, ' ')}
            </span>
          )}
          {creator.unique_hook && (
            <span className="text-[9px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1"
                  style={{ background: '#1a1a1a', color: P, border: `1px solid ${P}40` }}>
              <Hash className="h-2.5 w-2.5" /> {creator.unique_hook.replace(/^#/, '')}
            </span>
          )}
          {creator.payment_amount && (
            <span className="text-[9px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1 ml-auto"
                  style={{ background: 'rgba(74,222,128,0.1)', color: '#4ade80' }}>
              <DollarSign className="h-2.5 w-2.5" />
              ${Math.round(creator.payment_amount).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* KPI expectations */}
      {benchmarks && benchmarks.length > 0 ? (
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5">
              <Target className="h-3.5 w-3.5" style={{ color: T }} />
              <span className="text-[10px] font-black text-white uppercase tracking-widest">Your Targets</span>
            </div>
            {follower_weight_pct != null && (
              <span className="text-[9px] text-gray-500 tabular-nums">
                You represent <span className="font-bold text-white">{follower_weight_pct}%</span> of wave audience
              </span>
            )}
          </div>

          <div className={compact ? 'grid grid-cols-1 gap-2' : 'grid md:grid-cols-2 gap-2'}>
            {benchmarks.map((b) => {
              const cat = BENCHMARK_CATALOG.find(c => c.key === b.key);
              const Icon = cat?.icon || Target;
              const meta = STATUS_META[b.status] || STATUS_META.pending;
              const pct = Math.max(0, Math.min(100, Math.round(b.pct)));
              const fmt = cat?.format || ((v) => v);
              return (
                <div key={b.key} className="rounded-lg p-2.5"
                     style={{ background: '#0A0A0A', border: `1px solid ${meta.color}30` }}
                     data-testid={`creator-kpi-${b.key}`}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Icon className="h-3 w-3" style={{ color: meta.color }} />
                    <span className="text-[10px] font-bold text-white flex-1 truncate">{b.label}</span>
                    <span className="text-[8px] px-1.5 py-0.5 rounded-full font-black uppercase flex items-center gap-0.5"
                          style={{ background: meta.bg, color: meta.color }}>
                      <meta.icon className="h-2.5 w-2.5" /> {meta.label}
                    </span>
                  </div>
                  <div className="flex items-end justify-between mb-1">
                    <div>
                      <p className="text-[8px] uppercase text-gray-500 font-bold tracking-wider">Your pace</p>
                      <p className="text-sm font-black tabular-nums" style={{ color: meta.color }}>
                        {fmt(b.current)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[8px] uppercase text-gray-500 font-bold tracking-wider">Personal target</p>
                      <p className="text-xs font-bold text-gray-400 tabular-nums" style={{ }}>
                        {fmt(b.personal_target)}
                      </p>
                      <p className="text-[8px] text-gray-600">
                        ({fmt(b.full_target)} wave-wide)
                      </p>
                    </div>
                  </div>
                  <div className="h-1 rounded-full overflow-hidden" style={{ background: '#1a1a1a' }}>
                    <div className="h-full transition-all duration-700 rounded-full"
                         style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${meta.color}, ${pct >= 100 ? '#4ade80' : meta.color})` }} />
                  </div>
                  <p className="text-[8px] text-gray-500 mt-1 tabular-nums">{pct}% of target</p>
                </div>
              );
            })}
          </div>

          {/* Leaderboard rank */}
          {leaderboard_rank && leaderboard_total > 0 && (
            <div className="mt-3 rounded-lg p-2.5 flex items-center gap-2.5" data-testid="creator-leaderboard-rank"
                 style={{ background: 'linear-gradient(90deg, rgba(255,184,77,0.08), rgba(0,245,255,0.05))', border: '1px solid rgba(255,184,77,0.25)' }}>
              <Trophy className="h-4 w-4 text-[#FFB84D] flex-shrink-0" />
              <div className="flex-1">
                <p className="text-[11px] font-black text-white">
                  You're <span className="text-[#FFB84D]">#{leaderboard_rank}</span> of {leaderboard_total} creators firing right now
                </p>
                <p className="text-[9px] text-gray-500">
                  {leaderboard_rank === 1 ? 'Top performer — keep it going.' :
                   leaderboard_rank <= 3 ? 'Top tier — bonus eligible.' :
                   leaderboard_rank <= Math.ceil(leaderboard_total / 2) ? 'Above median — momentum building.' :
                   'Room to climb — more engagement = higher payout tier.'}
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="p-6 text-center">
          <UsersIcon className="h-6 w-6 mx-auto mb-2 text-gray-600" />
          <p className="text-[11px] text-gray-500">No KPI benchmarks set on this wave yet.</p>
        </div>
      )}
    </div>
  );
}
